import React, { useState, useMemo, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Tooltip,
  Space,
  Spin,
  Modal,
  Table,
} from "antd";
import { QuestionCircleOutlined, LoadingOutlined } from "@ant-design/icons";
import { Column } from "@ant-design/plots";
import moment from "moment";
import axios from "axios";
import ViewTypeSelector from "../AdminThroughputCharts/ViewTypeSelector";
import DateRangePicker from "../../DateRangePicker/DateRangePicker";
import ENVIRONMENT from "../../../../environments";
import useTableFilter from "../../UseTableFilter/useTableFilter";

const { Title } = Typography;
const DATE_FORMAT = "YYYY/MM/DD";

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const ConversionsChart = ({ chartStyle, dateRange, onDateChange }) => {
  const [viewType, setViewType] = useState("Daily");
  const [chartData, setChartData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalData, setModalData] = useState([]);
  const [isModalLoading, setIsModalLoading] = useState(false);

  const [getColumnSearchProps] = useTableFilter();

  const fetchConversionsData = async () => {
    if (!dateRange?.start || !dateRange?.end) return;
    try {
      setIsLoading(true);
      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
        action: "get_trial_to_paid_user_count",
        start_date: moment(dateRange.start).format("YYYY-MM-DD"),
        end_date: moment(dateRange.end).format("YYYY-MM-DD"),
      });
      const data = response.data || [];
      setChartData(
        data.map((item) => ({
          date: moment(item.date, "YYYY-MM-DD").format("YYYY/MM/DD"),
          conversions: parseInt(item.count, 10),
        }))
      );
    } catch (error) {
      console.error("Error fetching conversions data:", error);
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchConversionsData();
  }, [dateRange]);

  const processedData = useMemo(() => {
    if (viewType === "Daily") {
      return chartData.map((item) => ({
        ...item,
        periodStart: item.date,
        periodEnd: item.date,
      }));
    }

    if (viewType === "Weekly") {
      const weeklyData = new Map();
      chartData.forEach((item) => {
        const d = moment(item.date, "YYYY/MM/DD");
        const weekStart = d.clone().startOf("week");
        const weekEnd = d.clone().endOf("week");
        const key = weekStart.format("YYYY/MM/DD");
        if (!weeklyData.has(key)) {
          weeklyData.set(key, {
            date: `${weekStart.format("MMM DD")} - ${weekEnd.format("MMM DD")}`,
            conversions: 0,
            periodStart: weekStart.format("YYYY-MM-DD"),
            periodEnd: weekEnd.format("YYYY-MM-DD"),
            sortDate: key,
          });
        }
        weeklyData.get(key).conversions += item.conversions;
      });
      return Array.from(weeklyData.values()).sort((a, b) =>
        moment(a.sortDate, "YYYY/MM/DD").diff(moment(b.sortDate, "YYYY/MM/DD"))
      );
    }

    if (viewType === "Monthly") {
      const monthlyData = new Map();
      chartData.forEach((item) => {
        const d = moment(item.date, "YYYY/MM/DD");
        const monthStart = d.clone().startOf("month");
        const monthEnd = d.clone().endOf("month");
        const key = monthStart.format("YYYY/MM");
        if (!monthlyData.has(key)) {
          monthlyData.set(key, {
            date: monthStart.format("MMM YYYY"),
            conversions: 0,
            periodStart: monthStart.format("YYYY-MM-DD"),
            periodEnd: monthEnd.format("YYYY-MM-DD"),
            sortDate: monthStart.format("YYYY/MM/DD"),
          });
        }
        monthlyData.get(key).conversions += item.conversions;
      });
      return Array.from(monthlyData.values()).sort((a, b) =>
        moment(a.sortDate, "YYYY/MM/DD").diff(moment(b.sortDate, "YYYY/MM/DD"))
      );
    }

    return chartData;
  }, [chartData, viewType]);

  const handleBarClick = useCallback(async ({ data }) => {
    if (!data || data.conversions === 0) return;

    const { date, periodStart, periodEnd } = data;
    const start = periodStart
      ? moment(periodStart, "YYYY-MM-DD").format("YYYY-MM-DD")
      : moment(date, "YYYY/MM/DD").format("YYYY-MM-DD");
    const end = periodEnd
      ? moment(periodEnd, "YYYY-MM-DD").format("YYYY-MM-DD")
      : start;

    setModalTitle(`Conversions — ${date}`);
    setModalData([]);
    setIsModalOpen(true);
    setIsModalLoading(true);

    try {
      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, {
        action: "get_trial_to_paid_user_details",
        start_date: start,
        end_date: end,
      });
      setModalData(
        (response.data || []).map((item, i) => ({ ...item, key: i }))
      );
    } catch (err) {
      console.error("Error fetching conversion details:", err);
      setModalData([]);
    } finally {
      setIsModalLoading(false);
    }
  }, []);

  const chartConfig = useMemo(
    () => ({
      data: processedData,
      xField: "date",
      yField: "conversions",
      animation: false,
      color: "#52c41a",
      columnWidthRatio: 0.6,
      xAxis: {
        label: { autoRotate: true, autoHide: true },
      },
      yAxis: {
        label: { formatter: (v) => `${v}` },
        title: { text: "Conversions" },
      },
      tooltip: {
        formatter: (datum) => ({ name: "Conversions", value: datum.conversions }),
      },
      onReady: (plot) => {
        plot.on("element:click", ({ data }) => {
          if (data?.data) handleBarClick({ data: data.data });
        });
      },
    }),
    [processedData, handleBarClick]
  );

  const modalColumns = [
    {
      title: "Username",
      dataIndex: "username",
      key: "username",
      ...getColumnSearchProps("username"),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      ...getColumnSearchProps("email"),
    },
    {
      title: "Profile Name",
      dataIndex: "profile_name",
      key: "profile_name",
      ...getColumnSearchProps("profile_name"),
    },
    {
      title: "Company",
      dataIndex: "company_display_name",
      key: "company_display_name",
      ...getColumnSearchProps("company_display_name"),
    },
    {
      title: "Converted To",
      dataIndex: "subscription_name",
      key: "subscription_name",
    },
    {
      title: "Conversion Date",
      dataIndex: "conversion_date",
      key: "conversion_date",
    },
  ];

  return (
    <Col span={24}>
      <Card style={chartStyle}>
        <Row justify="space-between" align="middle">
          <Title level={4}>
            Trial to Paid Conversions{" "}
            <Tooltip title="Daily, weekly, or monthly count of free trial users converting to paid subscriptions. Click a bar to see who converted.">
              <QuestionCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </Title>
          <Row gutter={12}>
            <Col>
              <Space>
                <DateRangePicker
                  start={dateRange.start}
                  end={dateRange.end}
                  date_format={DATE_FORMAT}
                  onCalChange={onDateChange}
                  disabledDate={(current) => current && current.isBefore(moment('2026-01-01'), 'day')}
                />
                <ViewTypeSelector value={viewType} onChange={setViewType} />
              </Space>
            </Col>
          </Row>
        </Row>

        <Divider style={{ margin: "16px 0" }} />
        <Spin spinning={isLoading} indicator={antIcon}>
          <Column {...chartConfig} />
        </Spin>
      </Card>

      <Modal
        title={modalTitle}
        visible={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setModalData([]);
        }}
        footer={null}
        width="80vw"
        destroyOnClose
        centered
      >
        <Table
          columns={modalColumns}
          dataSource={modalData}
          loading={{ indicator: antIcon, spinning: isModalLoading }}
          pagination={false}
          scroll={{ y: 400 }}
          size="middle"
        />
      </Modal>
    </Col>
  );
};

export default ConversionsChart;
